import React, { createContext, useCallback, useContext, useState } from 'react';
import type { TimeRange } from '../components/TimeRangeSelector';

export type DashboardScope = 'myWork' | 'team';

export interface DashboardFiltersState {
  timeRange: TimeRange;
  scope: DashboardScope;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

/** Convert dashboard time range to start/end dates for filtering widget data */
export function getDateRangeFromTimeRange(timeRange: TimeRange): DateRange {
  const endDate = new Date();
  const startDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  startDate.setHours(0, 0, 0, 0);

  switch (timeRange) {
    case 'today':
      break;
    case 'week':
      startDate.setDate(startDate.getDate() - 7);
      break;
    case 'month':
      startDate.setMonth(startDate.getMonth() - 1);
      break;
    case 'quarter':
      startDate.setMonth(startDate.getMonth() - 3);
      break;
    case 'year':
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
    case 'all':
      startDate.setFullYear(2000, 0, 1);
      break;
    default:
      startDate.setMonth(startDate.getMonth() - 1);
  }
  return { startDate, endDate };
}

/** Range for "upcoming" widgets: from now to end of the selected period in the future */
export function getUpcomingRangeFromTimeRange(timeRange: TimeRange): { startDate: Date; endDate: Date } {
  const startDate = new Date();
  const endDate = new Date();
  startDate.setSeconds(0, 0);
  endDate.setHours(23, 59, 59, 999);

  switch (timeRange) {
    case 'today':
      break;
    case 'week':
      endDate.setDate(endDate.getDate() + 7);
      break;
    case 'month':
      endDate.setMonth(endDate.getMonth() + 1);
      break;
    case 'quarter':
      endDate.setMonth(endDate.getMonth() + 3);
      break;
    case 'year':
      endDate.setFullYear(endDate.getFullYear() + 1);
      break;
    case 'all':
      endDate.setFullYear(endDate.getFullYear() + 10);
      break;
    default:
      endDate.setMonth(endDate.getMonth() + 1);
  }
  return { startDate, endDate };
}

function formatDateShort(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Human-readable period for the current time range (e.g. "Jan 3 – Jan 10, 2026") */
export function formatPeriodForTimeRange(timeRange: TimeRange): string {
  const { startDate, endDate } = getDateRangeFromTimeRange(timeRange);
  const startStr = formatDateShort(startDate);
  const endStr = formatDateShort(endDate);
  if (startStr === endStr) return startStr;
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  if (startYear !== endYear) return `${startStr} – ${endStr}`;
  const startPart = startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${startPart} – ${endStr}`;
}

const defaultFilters: DashboardFiltersState = {
  timeRange: 'month',
  scope: 'myWork',
};

export interface DashboardFiltersContextValue extends DashboardFiltersState {
  setTimeRange: (range: TimeRange) => void;
  setScope: (scope: DashboardScope) => void;
}

const DashboardFiltersContext = createContext<DashboardFiltersContextValue | null>(null);

export const DashboardFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [timeRange, setTimeRangeState] = useState<TimeRange>(defaultFilters.timeRange);
  const [scope, setScopeState] = useState<DashboardScope>(defaultFilters.scope);

  const setTimeRange = useCallback((range: TimeRange) => {
    setTimeRangeState(range);
  }, []);

  const setScope = useCallback((s: DashboardScope) => {
    setScopeState(s);
  }, []);

  const value: DashboardFiltersContextValue = {
    timeRange,
    scope,
    setTimeRange,
    setScope,
  };

  return (
    <DashboardFiltersContext.Provider value={value}>
      {children}
    </DashboardFiltersContext.Provider>
  );
};

export function useDashboardFilters(): DashboardFiltersContextValue {
  const ctx = useContext(DashboardFiltersContext);
  if (!ctx) {
    return {
      ...defaultFilters,
      setTimeRange: () => {},
      setScope: () => {},
    };
  }
  return ctx;
}
